import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, type CreatorProfile } from '../../lib/supabase';
import { format } from 'date-fns';
import AdminLayout from '../../components/feature/AdminLayout';

const BANK_LIST = [
  '台灣銀行',
  '土地銀行',
  '合作金庫',
  '第一銀行',
  '華南銀行',
  '彰化銀行',
  '上海商銀',
  '台北富邦',
  '國泰世華',
  '高雄銀行',
  '兆豐銀行',
  '花旗銀行',
  '台新銀行',
  '永豐銀行',
  '玉山銀行',
  '凱基銀行',
  '星展銀行',
  '台中銀行',
  '京城銀行',
  '滙豐銀行',
  '中國信託',
  '聯邦銀行',
  '遠東銀行',
  '元大銀行',
  '新光銀行',
  '陽信銀行',
  '三信銀行',
  '郵局',
];

interface ProfileChangeRequest {
  id: string;
  creator_id: string;
  field_name: string;
  old_value: string;
  new_value: string;
  change_type: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_by: string;
  admin_note?: string;
  created_at: string;
  updated_at: string;
}

export default function AdminCreators() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creators, setCreators] = useState<CreatorProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreator, setSelectedCreator] = useState<CreatorProfile | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // 修改鎖定資料的狀態
  const [editLockedPhone, setEditLockedPhone] = useState('');
  const [editLockedBankName, setEditLockedBankName] = useState('');
  const [editLockedBankBranch, setEditLockedBankBranch] = useState('');
  const [editLockedBankAccountName, setEditLockedBankAccountName] = useState('');
  const [editLockedBankAccountNumber, setEditLockedBankAccountNumber] = useState('');
  const [editLockedAdminNote, setEditLockedAdminNote] = useState('');
  const [submittingLockedData, setSubmittingLockedData] = useState(false);

  // 待確認的變更請求
  const [pendingChangeRequest, setPendingChangeRequest] = useState<ProfileChangeRequest[] | null>(null);

  const ADMIN_EMAILS = ['yirun0206@gmail.com'];

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }
    if (!ADMIN_EMAILS.includes(user.email || '')) {
      navigate('/');
      return;
    }
    loadCreators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadCreators = async () => {
    try {
      const { data, error } = await supabase
        .from('creator_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCreators(data ?? []);
    } catch (error) {
      console.error('Error loading creators:', error);
    } finally {
      setLoading(false);
    }
  };

  // 送出鎖定資料變更請求
  const submitLockedDataChange = async () => {
    if (!selectedCreator) return;

    // 檢查是否有任何變更
    const hasChanges = 
      editLockedPhone !== (selectedCreator.phone || '') ||
      editLockedBankName !== (selectedCreator.bank_name || '') ||
      editLockedBankBranch !== (selectedCreator.bank_branch || '') ||
      editLockedBankAccountName !== (selectedCreator.bank_account_name || '') ||
      editLockedBankAccountNumber !== (selectedCreator.bank_account_number || '');

    if (!hasChanges) {
      alert('沒有任何變更');
      return;
    }

    if (!window.confirm('確定要送出變更請求嗎？創作者需要確認後才會生效。')) {
      return;
    }

    setSubmittingLockedData(true);
    try {
      // 準備要插入的變更請求（每個欄位一筆記錄）
      const changeRequests = [];

      if (editLockedPhone !== (selectedCreator.phone || '')) {
        changeRequests.push({
          creator_id: selectedCreator.id,
          field_name: 'phone',
          old_value: selectedCreator.phone || '',
          new_value: editLockedPhone,
          change_type: 'admin_update',
          status: 'pending',
          requested_by: 'admin',
          admin_note: editLockedAdminNote || null
        });
      }

      if (editLockedBankName !== (selectedCreator.bank_name || '')) {
        changeRequests.push({
          creator_id: selectedCreator.id,
          field_name: 'bank_name',
          old_value: selectedCreator.bank_name || '',
          new_value: editLockedBankName,
          change_type: 'admin_update',
          status: 'pending',
          requested_by: 'admin',
          admin_note: editLockedAdminNote || null
        });
      }

      if (editLockedBankBranch !== (selectedCreator.bank_branch || '')) {
        changeRequests.push({
          creator_id: selectedCreator.id,
          field_name: 'bank_branch',
          old_value: selectedCreator.bank_branch || '',
          new_value: editLockedBankBranch,
          change_type: 'admin_update',
          status: 'pending',
          requested_by: 'admin',
          admin_note: editLockedAdminNote || null
        });
      }

      if (editLockedBankAccountName !== (selectedCreator.bank_account_name || '')) {
        changeRequests.push({
          creator_id: selectedCreator.id,
          field_name: 'bank_account_name',
          old_value: selectedCreator.bank_account_name || '',
          new_value: editLockedBankAccountName,
          change_type: 'admin_update',
          status: 'pending',
          requested_by: 'admin',
          admin_note: editLockedAdminNote || null
        });
      }

      if (editLockedBankAccountNumber !== (selectedCreator.bank_account_number || '')) {
        changeRequests.push({
          creator_id: selectedCreator.id,
          field_name: 'bank_account_number',
          old_value: selectedCreator.bank_account_number || '',
          new_value: editLockedBankAccountNumber,
          change_type: 'admin_update',
          status: 'pending',
          requested_by: 'admin',
          admin_note: editLockedAdminNote || null
        });
      }

      // 批次插入所有變更請求
      const { error } = await supabase
        .from('profile_change_requests')
        .insert(changeRequests);

      if (error) throw error;

      alert('變更請求已送出，等待創作者確認');
      
      // 清空表單
      setEditLockedPhone('');
      setEditLockedBankName('');
      setEditLockedBankBranch('');
      setEditLockedBankAccountName('');
      setEditLockedBankAccountNumber('');
      setEditLockedAdminNote('');

      // 重新載入待確認請求
      await loadPendingChangeRequest(selectedCreator.id);
    } catch (error) {
      console.error('Error submitting change request:', error);
      alert('送出失敗，請稍後再試');
    } finally {
      setSubmittingLockedData(false);
    }
  };

  // 撤銷變更請求
  const cancelChangeRequest = async () => {
    if (!pendingChangeRequest || pendingChangeRequest.length === 0) return;

    if (!window.confirm('確定要撤銷所有待確認的變更請求嗎？')) {
      return;
    }

    try {
      // 刪除所有待確認的變更請求
      const requestIds = pendingChangeRequest.map(req => req.id);
      const { error } = await supabase
        .from('profile_change_requests')
        .delete()
        .in('id', requestIds);

      if (error) throw error;

      alert('已撤銷變更請求');
      setPendingChangeRequest(null);
    } catch (error) {
      console.error('Error canceling change request:', error);
      alert('撤銷失敗，請稍後再試');
    }
  };

  // 載入待確認的變更請求
  const loadPendingChangeRequest = async (creatorId: string) => {
    try {
      const { data, error } = await supabase
        .from('profile_change_requests')
        .select('*')
        .eq('creator_id', creatorId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPendingChangeRequest(data && data.length > 0 ? data : null);
    } catch (error) {
      console.error('Error loading pending change request:', error);
    }
  };

  const openCreatorDetail = (creator: CreatorProfile) => {
    setSelectedCreator(creator);
    // 初始化編輯欄位為當前值
    setEditLockedPhone(creator.phone || '');
    setEditLockedBankName(creator.bank_name || '');
    setEditLockedBankBranch(creator.bank_branch || '');
    setEditLockedBankAccountName(creator.bank_account_name || '');
    setEditLockedBankAccountNumber(creator.bank_account_number || '');
    setEditLockedAdminNote('');
    // 載入待確認的變更請求
    loadPendingChangeRequest(creator.id);
  };

  const filtered =
    filter === 'all'
      ? creators
      : filter === 'active'
      ? creators.filter((c) => c.total_rewards > 0)
      : creators.filter((c) => c.total_rewards === 0);

  const stats = {
    total: creators.length,
    active: creators.filter((c) => c.total_rewards > 0).length,
    totalRewards: creators.reduce((sum, c) => sum + c.total_rewards, 0),
  };

  if (loading) {
    return (
      <AdminLayout title="創作者管理">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const labelCls = 'block text-slate-400 text-sm mb-2';
  const inputCls =
    'w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-teal-500 transition-colors';

  return (
    <AdminLayout title="創作者管理">
      {/* ===== 電腦版 ===== */}
      <div className="hidden md:block max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-lg mb-4">
              <i className="ri-team-line text-2xl text-teal-400"></i>
            </div>
            <p className="text-slate-400 text-sm mb-1">創作者總數</p>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/20 rounded-lg mb-4">
              <i className="ri-user-star-line text-2xl text-emerald-400"></i>
            </div>
            <p className="text-slate-400 text-sm mb-1">活躍創作者</p>
            <p className="text-3xl font-bold text-white">{stats.active}</p>
          </div>
          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-lg mb-4">
              <i className="ri-money-dollar-circle-line text-2xl text-amber-400"></i>
            </div>
            <p className="text-slate-400 text-sm mb-1">總發放獎金</p>
            <p className="text-3xl font-bold text-white">NT${stats.totalRewards.toLocaleString()}</p>
          </div>
        </div>

        <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-xl font-bold text-white">創作者列表</h2>
          </div>
          {creators.length === 0 ? (
            <div className="p-12 text-center">
              <i className="ri-user-line text-6xl text-slate-600 mb-4 block"></i>
              <p className="text-slate-400">暫無創作者資料</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      姓名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      電子郵件
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      手機號碼
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      總獎金
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      加入日期
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {creators.map((creator) => (
                    <tr key={creator.id} className="hover:bg-slate-700/30 transition-colors">
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">{creator.name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm">{creator.email}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm">
                          {creator.phone || '未設定'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-emerald-400 font-medium">
                          NT${creator.total_rewards.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm">
                          {format(new Date(creator.created_at), 'yyyy/MM/dd')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openCreatorDetail(creator)}
                          className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          查看詳情
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ===== 手機版 ===== */}
      <div className="md:hidden px-4 pt-4">
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-slate-900 rounded-xl p-3.5 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-team-line text-sm text-teal-400"></i>
              </div>
              <span className="text-slate-500 text-xs">總數</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-slate-900 rounded-xl p-3.5 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 flex items-center justify-center bg-emerald-500/20 rounded-lg">
                <i className="ri-user-star-line text-sm text-emerald-400"></i>
              </div>
              <span className="text-slate-500 text-xs">活躍</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.active}</p>
          </div>
          <div className="bg-slate-900 rounded-xl p-3.5 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 flex items-center justify-center bg-amber-500/20 rounded-lg">
                <i className="ri-money-dollar-circle-line text-sm text-amber-400"></i>
              </div>
              <span className="text-slate-500 text-xs">獎金</span>
            </div>
            <p className="text-xl font-bold text-white">
              NT${stats.totalRewards.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {creators.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 flex items-center justify-center bg-slate-800 rounded-full mb-3">
                <i className="ri-user-line text-2xl text-slate-600"></i>
              </div>
              <p className="text-slate-500 text-xs">暫無創作者資料</p>
            </div>
          ) : (
            creators.map((creator) => (
              <button
                key={creator.id}
                onClick={() => openCreatorDetail(creator)}
                className="w-full text-left bg-slate-900 rounded-xl px-4 py-3 border border-slate-800/60 active:bg-slate-800 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center bg-slate-800 rounded-full flex-shrink-0">
                      <i className="ri-user-line text-sm text-slate-400"></i>
                    </div>
                    <span className="text-white text-sm font-medium truncate">
                      {creator.name}
                    </span>
                  </div>
                  <span className="text-emerald-400 text-xs font-medium flex-shrink-0 ml-2">
                    NT${creator.total_rewards.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="truncate flex-1">{creator.email}</span>
                  <span className="flex-shrink-0">
                    {format(new Date(creator.created_at), 'MM/dd')}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ===== 詳情彈窗 ===== */}
      {selectedCreator && (
        <>
          {/* 電腦版彈窗 */}
          <div
            className="hidden md:flex fixed inset-0 bg-black/70 items-center justify-center z-50 p-6"
            onClick={() => setSelectedCreator(null)}
          >
            <div
              className="bg-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-8 py-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">創作者詳情</h2>
                <button
                  onClick={() => setSelectedCreator(null)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-700 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-slate-400"></i>
                </button>
              </div>

              <div className="px-8 py-8 space-y-8">
                {/* 基本資料 */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4">基本資料</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">姓名</label>
                      <p className="text-white">{selectedCreator.name}</p>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">電子郵件</label>
                      <p className="text-white">{selectedCreator.email}</p>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">手機號碼</label>
                      <p className="text-white">{selectedCreator.phone || '未設定'}</p>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">加入日期</label>
                      <p className="text-white">
                        {format(new Date(selectedCreator.created_at), 'yyyy/MM/dd HH:mm')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 銀行資料 */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4">銀行資料</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">銀行名稱</label>
                      <p className="text-white">{selectedCreator.bank_name || '未設定'}</p>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">分行</label>
                      <p className="text-white">{selectedCreator.bank_branch || '未設定'}</p>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">戶名</label>
                      <p className="text-white">
                        {selectedCreator.bank_account_name || '未設定'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">帳號</label>
                      <p className="text-white">
                        {selectedCreator.bank_account_number
                          ? `****${selectedCreator.bank_account_number.slice(-4)}`
                          : '未設定'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 獎金統計 */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4">獎金統計</h3>
                  <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">總獲得獎金</span>
                      <span className="text-2xl font-bold text-emerald-400">
                        NT${selectedCreator.total_rewards.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 修改鎖定資料 */}
                <div className="border-t border-slate-700 pt-8">
                  <h3 className="text-lg font-bold text-white mb-4">修改鎖定資料</h3>
                  
                  {/* 待確認的變更請求 */}
                  {pendingChangeRequest && pendingChangeRequest.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-amber-900 mb-1">待創作者確認的變更</h4>
                          <p className="text-sm text-amber-700">
                            已於 {new Date(pendingChangeRequest[0].created_at).toLocaleString('zh-TW')} 送出
                          </p>
                        </div>
                        <button
                          onClick={cancelChangeRequest}
                          className="px-4 py-2 text-sm bg-white border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors whitespace-nowrap"
                        >
                          撤銷變更
                        </button>
                      </div>

                      <div className="space-y-3">
                        {pendingChangeRequest.map((request) => {
                          const fieldLabels: Record<string, string> = {
                            phone: '手機號碼',
                            bank_name: '銀行名稱',
                            bank_branch: '分行',
                            bank_account_name: '戶名',
                            bank_account_number: '帳號'
                          };

                          return (
                            <div key={request.id} className="bg-white rounded-lg p-4 border border-amber-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900">
                                  {fieldLabels[request.field_name] || request.field_name}
                                </span>
                              </div>
                              <div className="text-sm space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">舊值：</span>
                                  <span className="text-gray-700">{request.old_value || '(空白)'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">新值：</span>
                                  <span className="text-teal-600 font-medium">{request.new_value}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {pendingChangeRequest[0].admin_note && (
                          <div className="bg-white rounded-lg p-4 border border-amber-200">
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">管理員備註：</span>
                              <p className="text-gray-600 mt-1">{pendingChangeRequest[0].admin_note}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-slate-900/30 rounded-lg p-6 border border-slate-700/50 space-y-4">
                    <p className="text-slate-400 text-sm mb-4">
                      填寫需要修改的欄位，送出後創作者需確認才會生效
                    </p>

                    {/* 手機號碼 */}
                    <div>
                      <label className={labelCls}>
                        手機號碼
                        {selectedCreator.phone && (
                          <span className="ml-2 text-slate-600 text-xs">目前：{selectedCreator.phone}</span>
                        )}
                      </label>
                      <input
                        type="tel"
                        value={editLockedPhone}
                        onChange={(e) => setEditLockedPhone(e.target.value)}
                        placeholder="09xxxxxxxx"
                        maxLength={10}
                        inputMode="numeric"
                        className={inputCls}
                      />
                    </div>

                    {/* 銀行名稱 */}
                    <div>
                      <label className={labelCls}>
                        銀行名稱
                        {selectedCreator.bank_name && (
                          <span className="ml-2 text-slate-600 text-xs">目前：{selectedCreator.bank_name}</span>
                        )}
                      </label>
                      <select
                        value={editLockedBankName}
                        onChange={(e) => setEditLockedBankName(e.target.value)}
                        className={`${inputCls} appearance-none cursor-pointer`}
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      >
                        <option value="">請選擇銀行</option>
                        {BANK_LIST.map((bank) => (
                          <option key={bank} value={bank}>{bank}</option>
                        ))}
                      </select>
                    </div>

                    {/* 分行 */}
                    <div>
                      <label className={labelCls}>
                        分行名稱
                        {selectedCreator.bank_branch && (
                          <span className="ml-2 text-slate-600 text-xs">目前：{selectedCreator.bank_branch}</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={editLockedBankBranch}
                        onChange={(e) => setEditLockedBankBranch(e.target.value)}
                        placeholder="例如：忠孝分行"
                        className={inputCls}
                      />
                    </div>

                    {/* 戶名 */}
                    <div>
                      <label className={labelCls}>
                        戶名
                        {selectedCreator.bank_account_name && (
                          <span className="ml-2 text-slate-600 text-xs">目前：{selectedCreator.bank_account_name}</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={editLockedBankAccountName}
                        onChange={(e) => setEditLockedBankAccountName(e.target.value)}
                        placeholder="請輸入戶名"
                        className={inputCls}
                      />
                    </div>

                    {/* 帳號 */}
                    <div>
                      <label className={labelCls}>
                        銀行帳號
                        {selectedCreator.bank_account_number && (
                          <span className="ml-2 text-slate-600 text-xs">目前：****{selectedCreator.bank_account_number.slice(-4)}</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={editLockedBankAccountNumber}
                        onChange={(e) => setEditLockedBankAccountNumber(e.target.value)}
                        placeholder="10-16位數字"
                        maxLength={16}
                        inputMode="numeric"
                        className={inputCls}
                      />
                    </div>

                    {/* 管理員備註 */}
                    <div>
                      <label className={labelCls}>管理員備註（選填，創作者可見）</label>
                      <textarea
                        value={editLockedAdminNote}
                        onChange={(e) => setEditLockedAdminNote(e.target.value)}
                        placeholder="例如：經電話確認需修改銀行帳戶"
                        maxLength={500}
                        rows={2}
                        className={`${inputCls} resize-none`}
                      />
                    </div>

                    <button
                      onClick={submitLockedDataChange}
                      disabled={submittingLockedData || !!pendingChangeRequest}
                      className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors cursor-pointer"
                    >
                      {submittingLockedData ? '送出中...' : pendingChangeRequest ? '已有待確認的請求' : '送出變更請求'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 手機版全屏 */}
          <div className="md:hidden fixed inset-0 z-50 bg-slate-950 flex flex-col animate-slideUp">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/60">
              <button
                onClick={() => setSelectedCreator(null)}
                className="flex items-center gap-1 text-teal-400 text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>返回
              </button>
              <span className="text-white text-sm font-semibold">創作者詳情</span>
              <div className="w-14"></div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* 基本資料 */}
              <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-4">
                <h3 className="text-white font-semibold text-sm mb-3">基本資料</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-500 text-xs mb-1">姓名</p>
                    <p className="text-white text-sm">{selectedCreator.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-1">電子郵件</p>
                    <p className="text-white text-sm">{selectedCreator.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-1">手機號碼</p>
                    <p className="text-white text-sm">{selectedCreator.phone || '未設定'}</p>
                  </div>
                </div>
              </div>

              {/* 銀行資料 */}
              <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-4">
                <h3 className="text-white font-semibold text-sm mb-3">銀行資料</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-500 text-xs mb-1">銀行名稱</p>
                    <p className="text-white text-sm">{selectedCreator.bank_name || '未設定'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-1">分行</p>
                    <p className="text-white text-sm">{selectedCreator.bank_branch || '未設定'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-1">戶名</p>
                    <p className="text-white text-sm">
                      {selectedCreator.bank_account_name || '未設定'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-1">帳號</p>
                    <p className="text-white text-sm">
                      {selectedCreator.bank_account_number
                        ? `****${selectedCreator.bank_account_number.slice(-4)}`
                        : '未設定'}
                    </p>
                  </div>
                </div>
              </div>

              {/* 獎金統計 */}
              <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-4">
                <h3 className="text-white font-semibold text-sm mb-3">獎金統計</h3>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-xs">總獲得獎金</span>
                  <span className="text-xl font-bold text-emerald-400">
                    NT${selectedCreator.total_rewards.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* 修改鎖定資料 */}
              <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-4">
                <h3 className="text-white font-semibold text-sm mb-3">修改鎖定資料</h3>
                
                {/* 如果有待確認的變更請求 */}
                {pendingChangeRequest && pendingChangeRequest.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <i className="ri-time-line text-amber-400 text-base flex-shrink-0 mt-0.5"></i>
                      <div className="flex-1">
                        <p className="text-amber-300 font-medium text-xs mb-2">有待確認的變更請求</p>
                        <div className="text-xs text-amber-200/80 space-y-1">
                          {pendingChangeRequest.map((request) => {
                            const fieldLabels: Record<string, string> = {
                              phone: '手機號碼',
                              bank_name: '銀行名稱',
                              bank_branch: '分行',
                              bank_account_name: '戶名',
                              bank_account_number: '帳號'
                            };
                            return (
                              <p key={request.id}>• {fieldLabels[request.field_name] || request.field_name}：{request.new_value}</p>
                            );
                          })}
                          {pendingChangeRequest[0].admin_note && (
                            <p className="mt-2 pt-2 border-t border-amber-500/20">
                              備註：{pendingChangeRequest[0].admin_note}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={cancelChangeRequest}
                          className="mt-2 px-2.5 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded text-xs font-medium transition-colors cursor-pointer"
                        >
                          撤銷此請求
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <p className="text-slate-500 text-xs mb-3">
                    填寫需要修改的欄位，送出後創作者需確認才會生效
                  </p>

                  {/* 手機號碼 */}
                  <div>
                    <label className="block text-slate-500 text-xs mb-1.5">
                      手機號碼
                      {selectedCreator.phone && (
                        <span className="ml-2 text-slate-600">目前：{selectedCreator.phone}</span>
                      )}
                    </label>
                    <input
                      type="tel"
                      value={editLockedPhone}
                      onChange={(e) => setEditLockedPhone(e.target.value)}
                      placeholder="09xxxxxxxx"
                      maxLength={10}
                      inputMode="numeric"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>

                  {/* 銀行名稱 */}
                  <div>
                    <label className="block text-slate-500 text-xs mb-1.5">
                      銀行名稱
                      {selectedCreator.bank_name && (
                        <span className="ml-2 text-slate-600">目前：{selectedCreator.bank_name}</span>
                      )}
                    </label>
                    <select
                      value={editLockedBankName}
                      onChange={(e) => setEditLockedBankName(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500 appearance-none cursor-pointer"
                      style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                    >
                      <option value="">請選擇銀行</option>
                      {BANK_LIST.map((bank) => (
                        <option key={bank} value={bank}>{bank}</option>
                      ))}
                    </select>
                  </div>

                  {/* 分行 */}
                  <div>
                    <label className="block text-slate-500 text-xs mb-1.5">
                      分行名稱
                      {selectedCreator.bank_branch && (
                        <span className="ml-2 text-slate-600">目前：{selectedCreator.bank_branch}</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={editLockedBankBranch}
                      onChange={(e) => setEditLockedBankBranch(e.target.value)}
                      placeholder="例如：忠孝分行"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>

                  {/* 戶名 */}
                  <div>
                    <label className="block text-slate-500 text-xs mb-1.5">
                      戶名
                      {selectedCreator.bank_account_name && (
                        <span className="ml-2 text-slate-600">目前：{selectedCreator.bank_account_name}</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={editLockedBankAccountName}
                      onChange={(e) => setEditLockedBankAccountName(e.target.value)}
                      placeholder="請輸入戶名"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>

                  {/* 帳號 */}
                  <div>
                    <label className="block text-slate-500 text-xs mb-1.5">
                      銀行帳號
                      {selectedCreator.bank_account_number && (
                        <span className="ml-2 text-slate-600">目前：****{selectedCreator.bank_account_number.slice(-4)}</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={editLockedBankAccountNumber}
                      onChange={(e) => setEditLockedBankAccountNumber(e.target.value)}
                      placeholder="10-16位數字"
                      maxLength={16}
                      inputMode="numeric"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>

                  {/* 管理員備註 */}
                  <div>
                    <label className="block text-slate-500 text-xs mb-1.5">管理員備註（選填）</label>
                    <textarea
                      value={editLockedAdminNote}
                      onChange={(e) => setEditLockedAdminNote(e.target.value)}
                      placeholder="例如：經電話確認需修改銀行帳戶"
                      maxLength={500}
                      rows={2}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500 resize-none"
                    />
                  </div>

                  <button
                    onClick={submitLockedDataChange}
                    disabled={submittingLockedData || !!pendingChangeRequest}
                    className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                  >
                    {submittingLockedData ? '送出中...' : pendingChangeRequest ? '已有待確認的請求' : '送出變更請求'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slideUp { animation: slideUp 0.25s ease-out; }
      `}</style>
    </AdminLayout>
  );
}
